<template>
    <div class="select-x-container">
        <div class="select-x sm" :class="{'focus': inputState === 'focus', 'has-items': items.length > 0}" ref="selectX">
            <span class="tag"
                  :style="tagStyle"
                  :class="{removed: item.removed, 'mark-to-remove': item.mark_to_remove}"
                  v-for="(item, idx) in value"
                  :key="idx">
                <span class="ellipsis">{{item.label}}</span>
                <a href="javascript:void(0)" @click="onRemoveItem(item, idx)">
                    <svg-icon icon="times"/>
                </a>
            </span>
            <input type="text"
                   ref="input"
                   :id="inputId"
                   :placeholder="value.length === 0 ? placeholder : ''"
                   v-model="model"
                   @focus="inputFocus"
                   @blur="inputBlur"
                   @keypress.enter="onKeyEnterPress"
                   @keyup="onKeyUp"
                   @keydown.down="moveCursor(false)"
                   @keydown.up="moveCursor(true)"
                   :class="inputClass"/>
        </div>
        <field-messages :errors="errors.input"/>

        <ul :style="listStyle">
            <li v-for="(item, idx) in itemsFilter"
                :key="idx"
                :class="{active: idx === selectedIndex, 'has-selected': item.selected}"
                @mouseover="selectedIndex = idx"
                @mousedown="selectItem"
                v-html="itemPresentation(item, value)"></li>
        </ul>
    </div>
</template>

<script>
    import {isArray, isFunc, isRegExp, isString, DefaultRegExps} from '@/utils/Utils'

    const Validators = {DefaultRegExps};

    export default {
        name: "SelectX",
        props: {
            inputId: {
                default: 'selectXInput'
            },
            inputClass: {
                type: [String, Object, Array],
                default: ''
            },
            value: {
                type: Array,
                default: () => [],
                validator: val => isArray(val)
            },
            placeholder: {
                type: String,
            },
            itemPresentation: {
                type: Function,
                default: val => val.label.replaceTags()

            },
            dataSource: {
                type: [Array, String],
                default: () => [],
                required: true,
                validator(val) {
                    if (isArray(val)) {
                        for (let i = 0; i < val.length; i++) {
                            if (!isString(val[i].label)) return false;
                        }

                        return true
                    }

                    return isString(val)
                }
            },
            allowIncludeNew: {
                type: [Boolean, String],
                default: true,
                validator: val => val === true || /(true|false)/gi.test(val.toLowerCase())
            },
            validatorInput: {
                type: [Object, String, Function],
                default: null,
                validator: val => {
                    return (isString(val) && /(email)/gi.test(val)) || (!val || isRegExp(val) || isFunc(val));
                }
            }
        },
        data() {
            return {
                inputState: null,
                timeoutMarkRemove: null,
                timeoutCleanMessages: null,
                timeoutFilter: null,
                listStyle: {},
                tagStyle: {},
                selectedIndex: -1,
                items: [],
                removing: false,
                errors: {input: []},
                model: '',
                serverCache: {}
            }
        },
        methods: {
            inputFocus() {
                this.inputState = 'focus';
                this.listStyle = {
                    width: `${this.$refs.selectX.offsetWidth}px`
                };

                this.tagStyle = {
                    maxWidth: `${this.$refs.selectX.offsetWidth - 8}px`
                };

                if (isArray(this.dataSource)) this.triggerAsyncFilter(0);
            },
            inputBlur() {
                this.inputState = null
            },
            moveCursor(isUp) {
                const len = this.itemsFilter.length;
                if (len > 0) {
                    let idx = parseInt(this.selectedIndex);
                    idx += isUp ? -1 : 1;

                    if (idx < 0) idx = len - 1;
                    else if (idx >= len) idx = 0;

                    this.selectedIndex = idx;
                }
            },
            onKeyEnterPress(e) {
                e.preventDefault();
                e.cancelBubble = true;

                if (`${this.allowIncludeNew}`.toLowerCase() === 'true' && this.validateInput() && this.value.indexById({id: e.target.value}) === -1) {
                    this.value.push({
                        id: e.target.value,
                        label: e.target.value
                    });

                    this.model = '';
                    this.$emit('input', this.value);
                } else if (this.selectedIndex > -1) {
                    this.selectItem();
                    this.model = '';
                } else {
                    this.timerCleanerValidations()
                }

                return false;
            },
            validateInput() {
                const val = (this.$refs.input.value || '').trim();

                if (val !== '') {
                    if (this.validatorInput) {
                        if (isString(this.validatorInput)) {
                            if (!Validators[this.validatorInput].test(val)) {
                                this.addValidationMessage('formato de e-mail inválido');
                                return false;
                            }
                            return true;
                        }

                        if(isRegExp(this.validatorInput)) {
                            if (!this.validatorInput.test(val)) {
                                this.addValidationMessage('formato de entrada inválido');
                                return false;
                            }

                            return true
                        }

                        if(isFunc(this.validatorInput)) {
                            if(!this.validatorInput(val)) {
                                this.addValidationMessage('formato de entrada inválido');
                                return false;
                            }

                            return true
                        }

                        return false;
                    }
                    return true;
                }

                return false
            },
            addValidationMessage(msg) {
                if(this.errors.input.length > 0) this.$set(this.errors.input, 0, msg);
                else this.errors.input.push(msg);
            },
            timerCleanerValidations() {
                clearTimeout(this.timeoutCleanMessages);

                this.timeoutCleanMessages = setTimeout(() => {
                    this.errors.input.forEach((inp, idx) => this.$delete(this.errors.input, idx))
                }, 4000)
            },
            onRemoveItem(item, idx) {
                if (this.removing) return false;

                this.removing = true;
                this.$set(item, 'removed', true);
                if(this.timeoutMarkRemove) clearTimeout(this.timeoutMarkRemove);

                setTimeout(() => {
                    this.$delete(this.value, idx);
                    this.$emit('input', this.value);
                    this.$emit('onRemoveItem', item);
                    this.removing = false;
                }, 200)
            },
            onKeyUp(e){
                if (e.key === 'Backspace' && (e.target.value || '').trim() === '') {
                    const val = (e.target.value || '').trim();
                    const idx = this.value.length - 1;

                    if (val === '' &&  idx > -1) {
                        const item = this.value[idx];

                        if (item.mark_to_remove) {
                            this.onRemoveItem(item, idx);
                        } else {
                            this.$set(item, 'mark_to_remove', true);
                            this.timeoutMarkRemove = setTimeout(() => item.mark_to_remove = false, 1000)
                        }
                    }
                } else {
                    this.triggerAsyncFilter()
                }
            },
            triggerAsyncFilter(timeout = 500) {
                clearTimeout(this.timeoutFilter);

                this.timeoutFilter = setTimeout(() => this.filter(), timeout);
            },
            selectItem() {
                if (this.selectedIndex > -1) {
                    const item = this.itemsFilter[this.selectedIndex];

                    if (item && this.value.indexById(item) === -1) {
                        this.value.push({
                            id: item.id || Math.random(),
                            label: item.label
                        });

                        this.$emit('input', this.value);
                    }
                }
            },
            filter() {
                if(isArray(this.dataSource)) {
                    this.items = this.dataSource.filter(i => i.label.toLowerCase().indexOf(this.model.toLowerCase()) > -1);
                } else {
                    const cacheKey = this.model === '' ? '_' : this.model;
                    const cache = this.serverCache[cacheKey];

                    if (cache && isArray(cache.data) && (new Date().getTime() - cache.timestamp) < 1000 * 60) {
                        this.items = [];
                        cache.data.forEach(d => this.items.push({
                            id: d.id,
                            label: d.name
                        }));
                    } else {
                        const params = new URLSearchParams({
                            criteria: this.model
                        });

                        this.$appHttp.get(`${this.dataSource}?${params.toString()}`)
                            .then(({data}) => {

                                this.$set(this.serverCache, cacheKey, {
                                    data: data,
                                    timestamp: new Date().getTime()
                                });

                                this.items = [];
                                data.forEach(d => this.items.push({
                                    id: d.id,
                                    label: d.name
                                }));
                            })
                            .catch(err => {
                                this.$snotify.error('Falha na chamada da API', this.$appStorage.getUser().name);
                                console.error(err);
                            });
                    }
                }
            }
        },
        mounted() {
            if (isArray(this.dataSource))
                this.dataSource.forEach((ds, idx) => this.$set(this.items, idx, ds));
            else if (isString(this.dataSource)){
                this.filter();
            }
        },
        computed: {
            itemsFilter() {
                const val = (this.model || '').trim().toLowerCase();
                let result = this.items;

                if (val !== '') result = this.items.filter(i => i.label.toLowerCase().indexOf(val) > -1);

                result.forEach(i => i.selected = this.value.indexById({id: i.id}) > -1);

                return result;
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "../../assets/css/variables";

    .select-x-container {
        position: relative;

        .select-x {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: flex-start;
            box-sizing: border-box;
            width: 100%;
            height: auto;
            font-size: 1rem;
            font-weight: 400;
            color: #495057;
            background-color: #fff;
            background-clip: padding-box;
            border: 1px solid #ced4da;
            border-radius: .25rem;
            transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

            &.sm {
                min-height: 28px;
                font-size: 0.875rem;
                line-height: 1.5;
                border-radius: 0.2rem;
            }

            &.focus {
                box-shadow: 0 0 0 0.2rem rgba(darken($theme_color_one, 10), .25);
                border-color: $theme_color_one;

                &.has-items {
                    outline: 0;
                    box-shadow: 0 0 0 0.2rem rgba(darken($theme_color_one, 10), .25);
                    border-bottom: none;
                    border-radius: 3px 3px 0 0;
                }
            }

            .tag {
                display: flex;
                align-items: center;
                background-color: $theme_color_one;
                border: 1px solid rgba(darken($theme_color_one, 10), .25);
                padding-left: 5px;
                padding-right: 5px;
                color: #fff;
                border-radius: 3px;
                font-size: 0.8rem;
                height: 20px;
                line-height: 20px;
                user-select: none;
                margin: 2px;
                transition: background-color .2s ease;

                a {
                    text-decoration: none;
                    color: #fff;
                    transition: color .2s ease;

                    &:hover {
                        color: $theme_color_six;
                    }

                    svg {
                        margin-left: 5px;
                        width: 0.8rem;
                        height: 0.8rem;
                        cursor: pointer;
                    }
                }

                &.removed {
                    animation: onRemoveTag ease;
                    animation-duration: .2s;
                    animation-fill-mode: forwards;
                }

                &.mark-to-remove {
                    background-color: rgba(darken($theme_color_one, 10), .25);
                }
            }

            input {
                flex-grow: 1;
                background-color: transparent;
                display: inline-block;
                border: none;
                outline: none;
                width: 0;
                padding: 0 0 0 5px;

                min-width: 50px;
                max-width: 100%;
            }
        }

        ul {
            position: absolute;
            list-style: none;
            padding-inline-start: unset!important;
            margin: 0;
            padding: 0;
            transition: background-color .2s linear, height .2s ease-in-out, opacity .2s linear;
            color: #000;
            box-sizing: border-box;
            background-color: rgba(255, 255, 255, .2);
            border-bottom: 1px solid $theme_color_one;
            border-left: 1px solid $theme_color_one;
            border-right: 1px solid $theme_color_one;
            border-radius: 0 0 3px 3px;
            z-index: 10;
            height: 0;
            max-height: 400px;
            overflow-y: auto;
            overflow-x: hidden;
            opacity: 0;

            li {
                padding: 5px;
                cursor: pointer;
                overflow-x: hidden;
                background-color: #fff;
                transition: background-color .2s ease;
                font-size: .8em;

                &.active {
                    background-color: $theme_color_one;
                    color: $theme_color_four;
                }

                &.has-selected {
                    background-color: $theme_color_eleven;
                    color: #000;
                }
            }
        }

        .select-x.focus.has-items + ul {
            animation: listIn .2s linear;
            animation-fill-mode: forwards;
        }
    }

    @keyframes listIn {
        to {
            background-color: #fff;
            border-bottom: 1px solid $theme_color_one;
            border-left: 1px solid $theme_color_one;
            border-right: 1px solid $theme_color_one;
            height: auto;
            opacity: 1;
            box-shadow: 0 15px 25px rgba(0, 0, 0, .6);
        }
    }

    @keyframes onRemoveTag {
        to {
            width: 0;
            height: 0;
            opacity: 0;
        }
    }
</style>
